import React, { useState, useEffect } from "react";
import { wsRequest } from "./wsClient.js";
import OtpCodeInput from "./OtpCodeInput.jsx";

// Opened from ProfilePage.jsx — either proactively from the Payment
// Details tab's own "Verify your phone" button, or contextually from
// a specific unpaid win in the Winnings list. Not tied to any
// participation action being blocked (see requireWinnerPhoneVerified
// on the backend for what this actually gates: prize payout,
// checked in the admin panel, not purchase/guess/free-entry). A
// global, centered overlay rather than one of this app's usual
// corner-anchored floating panels (like the guess form's own login
// prompt), since it can be triggered from more than one place on the
// same page. Dismissable: closing it just means verification hasn't
// happened yet, nothing about browsing the rest of the site is
// affected.
// Opened from ProfilePage.jsx — either from the Account tab's own
// inline phone field (passing initialPhone, since the number is
// already known there — skips straight to sending the code) or
// contextually from a specific unpaid win in the Winnings list
// (no initialPhone, since the user may never have entered a number
// at all — falls back to asking for one first, same as always). Not
// tied to any participation action being blocked (see
// requireWinnerPhoneVerified on the backend for what this actually
// gates: prize payout, checked in the admin panel, not
// purchase/guess/free-entry). A global, centered overlay rather than
// one of this app's usual corner-anchored floating panels (like the
// guess form's own login prompt), since it can be triggered from
// more than one place on the same page. Dismissable: closing it just
// means verification hasn't happened yet, nothing about browsing the
// rest of the site is affected.
export default function PhoneVerificationModal({ token, initialPhone, onVerified, onClose }) {
  const [step, setStep] = useState(initialPhone ? "enter-code" : "enter-phone"); // "enter-phone" | "enter-code"
  const [phone, setPhone] = useState(initialPhone || "");
  const [code, setCode] = useState("");
  // Already busy on mount when initialPhone is provided — the first
  // send kicks off immediately, before the person sees anything.
  const [busy, setBusy] = useState(!!initialPhone);
  const [error, setError] = useState(null);
  // Mirrors the backend's own 60s resend cooldown (see
  // PHONE_VERIFICATION_RESEND_COOLDOWN_MS) purely for the countdown
  // display — the backend enforces the real limit regardless of
  // whatever this local timer shows.
  const [resendCooldown, setResendCooldown] = useState(0);
  // Only ever present in the response when the mock (testing) SMS
  // provider is active — see requestPhoneVerification's own comment
  // on the backend for why this is safe to trust rather than a risk:
  // a real provider never includes this field at all, so there's
  // nothing to accidentally expose here for anyone using a real one.
  const [devCode, setDevCode] = useState(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown > 0]);

  // Auto-sends once, on mount, only when a phone number was already
  // supplied — the inline-field entry point's whole point is
  // skipping a redundant "now type your number again" step.
  useEffect(() => {
    if (initialPhone) sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendCode = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await wsRequest("phone.requestVerification", { phone: phone.trim() }, token);
      setStep("enter-code");
      setCode("");
      setResendCooldown(60);
      setDevCode(result?.devCode || null);
    } catch (err) {
      setError(err.message || "Could not send code.");
    }
    setBusy(false);
  };

  const confirmCode = async (codeValue) => {
    setError(null);
    setBusy(true);
    try {
      await wsRequest("phone.confirmVerification", { code: codeValue }, token);
      onVerified?.();
    } catch (err) {
      setError(err.message || "Incorrect code.");
      setCode("");
    }
    setBusy(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
        background: "var(--overlay-color)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          background: "var(--bg-panel-alt)",
          padding: 28,
          borderRadius: 10,
          maxWidth: 360,
          width: "100%",
          boxShadow: "0 4px 20px var(--shadow-color)",
          border: "1px solid var(--border-color)",
          boxSizing: "border-box",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Verify your phone</h3>
        <p style={{ opacity: 0.75, fontSize: 13.5, marginBottom: 18 }}>
          {step === "enter-phone"
            ? "Phone verification is required before you can continue."
            : busy && !error
            ? `Sending a 6-digit code to ${phone}…`
            : `We sent a 6-digit code to ${phone}.`}
        </p>

        {step === "enter-phone" ? (
          <>
            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3, opacity: 0.8 }}>
              Phone number (include your country code)
            </label>
            <input
              type="text"
              placeholder="+14155551234"
              value={phone}
              disabled={busy}
              onChange={(e) => setPhone(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }}
            />
            {error && <p style={{ color: "var(--accent-red)", fontSize: 13, marginBottom: 12 }}>{error}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={sendCode} disabled={busy || !phone.trim()}>
                {busy ? "Sending…" : "Send code"}
              </button>
              <button onClick={onClose} disabled={busy}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {devCode && (
              <div
                style={{
                  border: "1px solid var(--accent-yellow)",
                  borderRadius: 6,
                  padding: 10,
                  marginBottom: 14,
                  fontSize: 13,
                }}
              >
                {/* SAYS WHAT IS ACTUALLY TRUE. This block appears when the
                    SMS provider is set to "mock" — an admin's choice, NOT
                    a mode. It used to name a mode instead, which was
                    wrong in both directions: mock is not one, and a LIVE
                    site whose provider was never configured showed that
                    wording, plus the code, to real buyers.

                    Found 15 Sep 2026 while checking whether buyers ever
                    see mode names. They should not; this was the only
                    place one leaked.

                    WORDED AROUND THE LITERAL PHRASES on purpose: a test
                    scans this file for them and cannot tell a JSX block
                    comment from markup. The same trick is used in
                    PartnersPanel, for the same reason. */}
                No message was sent — this site has no SMS provider configured. Your code is <strong>{devCode}</strong>.{" "}
                <button
                  type="button"
                  onClick={() => setCode(devCode)}
                  disabled={busy}
                  style={{ background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  Use this code
                </button>
              </div>
            )}
            <OtpCodeInput length={6} value={code} onChange={setCode} onComplete={confirmCode} autoFocus disabled={busy} />
            {error && <p style={{ color: "var(--accent-red)", fontSize: 13, marginTop: 12 }}>{error}</p>}
            <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center" }}>
              <button className="btn" onClick={() => confirmCode(code)} disabled={busy || code.length !== 6}>
                {busy ? "Verifying…" : "Verify"}
              </button>
              <button onClick={sendCode} disabled={busy || resendCooldown > 0}>
                {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend code"}
              </button>
            </div>
            <button
              onClick={() => {
                setStep("enter-phone");
                setError(null);
                setCode("");
                setDevCode(null);
              }}
              disabled={busy}
              style={{ background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0, fontSize: 12.5, marginTop: 14, textDecoration: "underline" }}
            >
              Use a different number
            </button>
            {" · "}
            <button onClick={onClose} disabled={busy} style={{ background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0, fontSize: 12.5, textDecoration: "underline" }}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
